import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { useForm, useFieldArray, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { HiOutlinePencil } from "react-icons/hi2";
import { MdError, MdWarning, MdOutlineCancel } from "react-icons/md";
import { FaRegSave } from "react-icons/fa";

import AnimatedMainContainer from "../../../components/AnimatedMainContainer";
import EventSelectionModal from "../../AdminDocuments/components/EventSelectionModal";
import ControlledInput from "../../../components/ControlledInputs/input.jsx";
import { useToast } from "../../../hooks/toast";
import { positionConfigurationSchema } from "./validationSchema";
import {
  validateNoOverlap,
  calculateConfiguredPositions,
} from "../../../utils/positionValidation";
import {
  getEventAreas,
  getPositionConfigurations,
  batchPositionConfiguration,
} from "../../../service/totemPositionConfiguration.service";
import {
  getReportFilterEventsByYearService,
  getReportFilterYearsService,
} from "../../../service/reportFilter.service";
import api from "../../../service";

import {
  Container,
  TitleContainer,
  Title,
  ActionButton,
  ContainerButtons,
} from "../styles";
import {
  ContentContainer,
  FormSection,
  SectionTitle,
  InputGroup,
  Label,
  PositionCounter,
  CounterLabel,
  CounterValue,
  ValidationMessage,
  FieldHint,
  RangesList,
  AddRangeButton,
} from "./styles";
import PositionRangeItem from "./PositionRange/index.jsx";
import { Spin } from "antd";

const TOTAL_POSITIONS_MIN = 1;
const LIST_ROUTE = "/admin/totem-position-configuration";

const TotemPositionConfiguration = () => {
  const intl = useIntl();
  const history = useHistory();
  const { eventId: eventIdParam } = useParams();
  const { token } = useSelector((state) => state.auth);
  const { addToast } = useToast();

  const hasEventIdParam = Boolean(eventIdParam);

  const [eventModalOpen, setEventModalOpen] = useState(!hasEventIdParam);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [listEvents, setListEvents] = useState([]);
  const [listYears, setListYears] = useState([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [isLoadingAreas, setIsLoadingAreas] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [isLoadingConfigurations, setIsLoadingConfigurations] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [allAreas, setAllAreas] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [existingConfigurations, setExistingConfigurations] = useState([]);
  const [maxPositions, setMaxPositions] = useState(null);

  const form = useForm({
    resolver: zodResolver(positionConfigurationSchema(intl, maxPositions)),
    mode: "onTouched",
    reValidateMode: "onChange",
    defaultValues: {
      totalPositions: "",
      ranges: [
        {
          areaId: null,
          positionStart: "",
          positionEnd: "",
          canEdit: true,
          canDelete: true,
        },
      ],
    },
  });

  const {
    control,
    formState,
    handleSubmit,
    setValue,
    reset,
    clearErrors,
    setError,
    setFocus,
  } = form;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "ranges",
  });

  const watchedRanges = useWatch({ control, name: "ranges" });
  const watchedTotalPositions = useWatch({ control, name: "totalPositions" });

  const rangesKey = JSON.stringify(watchedRanges);

  const configuredCount = useMemo(() => {
    return calculateConfiguredPositions(watchedRanges || []);
  }, [rangesKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPositionsValue = useMemo(
    () => parseInt(watchedTotalPositions, 10),
    [watchedTotalPositions]
  );

  const hasTotalPositionsValid = useMemo(() => {
    if (Number.isNaN(totalPositionsValue)) return false;
    return totalPositionsValue >= TOTAL_POSITIONS_MIN;
  }, [totalPositionsValue]);

  const overlapError = useMemo(
    () => validateNoOverlap(watchedRanges || []),
    [rangesKey] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const hasOverlap = overlapError !== null;

  const exceedsLimit = useMemo(() => {
    if (!maxPositions) return false;
    return configuredCount > maxPositions;
  }, [configuredCount, maxPositions]);

  const hasRangesWithData = useMemo(() => {
    if (!watchedRanges) return false;
    return watchedRanges.some((range) => {
      if (!range) return false;
      const hasArea = Boolean(range.areaId?.value);
      const hasStart =
        range.positionStart !== null &&
        range.positionStart !== undefined &&
        range.positionStart !== "";
      const hasEnd =
        range.positionEnd !== null &&
        range.positionEnd !== undefined &&
        range.positionEnd !== "";
      return hasArea || hasStart || hasEnd;
    });
  }, [rangesKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const validateRange = useCallback(
    (range) => {
      if (!range) return null;
      const hasArea = Boolean(range.areaId?.value);
      const hasStart =
        range.positionStart !== null &&
        range.positionStart !== undefined &&
        range.positionStart !== "";
      const hasEnd =
        range.positionEnd !== null &&
        range.positionEnd !== undefined &&
        range.positionEnd !== "";

      // regra atual: validação "de formato" só quando completo (para não ficar gritando enquanto digita)
      if (!hasArea || !hasStart || !hasEnd) return null;

      const start = parseInt(range.positionStart, 10);
      const end = parseInt(range.positionEnd, 10);

      if (Number.isNaN(start) || Number.isNaN(end)) {
        return intl.formatMessage({ id: "totem_position.invalid_position" });
      }

      if (start < 1) {
        return intl.formatMessage(
          { id: "totem_position.position_must_be_positive" },
          {
            position: intl.formatMessage({
              id: "totem_position.position_start_label",
            }),
          }
        );
      }

      if (end < 1) {
        return intl.formatMessage(
          { id: "totem_position.position_must_be_positive" },
          {
            position: intl.formatMessage({
              id: "totem_position.position_end_label",
            }),
          }
        );
      }

      if (start > end) {
        return intl.formatMessage({
          id: "totem_position.end_must_be_greater_than_start",
        });
      }

      return null;
    },
    [intl]
  );

  const validateExpansion = useCallback(
    (currentRange) => {
      if (!currentRange) return null;
      if (currentRange.canEdit !== false) return null;
      if (!currentRange.id) return null;

      const originalConfig = existingConfigurations.find(
        (c) => c.id === currentRange.id
      );
      if (!originalConfig) return null;

      const hasStart =
        currentRange.positionStart !== null &&
        currentRange.positionStart !== undefined &&
        currentRange.positionStart !== "";
      const hasEnd =
        currentRange.positionEnd !== null &&
        currentRange.positionEnd !== undefined &&
        currentRange.positionEnd !== "";
      if (!hasStart || !hasEnd) return null;

      const newStart = parseInt(currentRange.positionStart, 10);
      const newEnd = parseInt(currentRange.positionEnd, 10);
      if (Number.isNaN(newStart) || Number.isNaN(newEnd)) return null;

      const { positionStart: oldStart, positionEnd: oldEnd } = originalConfig;

      if (newStart > oldStart || newEnd < oldEnd) {
        return intl.formatMessage(
          { id: "totem_position.cannot_reduce_range_with_sessions" },
          {
            oldStart,
            oldEnd,
            area: currentRange.areaId?.label || originalConfig.areaName || "",
          }
        );
      }

      return null;
    },
    [existingConfigurations, intl]
  );

  const validateRangeComplete = useCallback(
    (range) => validateRange(range) || validateExpansion(range),
    [validateRange, validateExpansion]
  );

  const hasInvalidExpansion = useMemo(
    () =>
      watchedRanges?.some((range) => range && validateExpansion(range) !== null) ??
      false,
    [rangesKey, validateExpansion] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const selectedAreaIds = useMemo(
    () => watchedRanges?.map((r) => r && r.areaId?.value).filter(Boolean) ?? [],
    [rangesKey] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const hasAvailableAreas = useMemo(
    () => allAreas.some((area) => !selectedAreaIds.includes(area.value)),
    [allAreas, selectedAreaIds]
  );

  const isLastRangeComplete = useMemo(() => {
    if (!watchedRanges?.length) return true;
    const lastRange = watchedRanges[watchedRanges.length - 1];
    if (!lastRange) return false;

    const hasArea = Boolean(lastRange.areaId?.value);
    const hasStart =
      lastRange.positionStart !== null &&
      lastRange.positionStart !== undefined &&
      lastRange.positionStart !== "";
    const hasEnd =
      lastRange.positionEnd !== null &&
      lastRange.positionEnd !== undefined &&
      lastRange.positionEnd !== "";

    return hasArea && hasStart && hasEnd;
  }, [rangesKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const canAddNewRange = useMemo(() => {
    return (
      maxPositions &&
      configuredCount < maxPositions &&
      hasAvailableAreas &&
      isLastRangeComplete &&
      !hasOverlap &&
      !exceedsLimit &&
      !hasInvalidExpansion
    );
  }, [
    maxPositions,
    configuredCount,
    hasAvailableAreas,
    isLastRangeComplete,
    hasOverlap,
    exceedsLimit,
    hasInvalidExpansion,
  ]);

  const availableAreasForIndex = useCallback(
    (currentIndex) => {
      const selectedIds = (watchedRanges || [])
        .map((range, idx) =>
          idx !== currentIndex && range ? range.areaId?.value : null
        )
        .filter(Boolean);

      return allAreas.filter((area) => !selectedIds.includes(area.value));
    },
    [allAreas, rangesKey] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const showEventsLoadError = useCallback(() => {
    addToast({
      type: "error",
      title: intl.formatMessage({ id: "error" }),
      description: intl.formatMessage({
        id: "totem_position.error_loading_events",
      }),
    });
  }, [addToast, intl]);

  const fetchYears = useCallback(() => {
    getReportFilterYearsService({ token })
      .then((response) => {
        setListYears(
          (response.data?.output ?? []).map((year) => ({
            label: String(year),
            value: year,
          }))
        );
      })
      .catch(() => {
        setListYears([]);
        showEventsLoadError();
      });
  }, [token, showEventsLoadError]);

  const fetchEventsByYear = useCallback(
    (year) => {
      setListEvents([]);

      if (year === null || year === undefined) {
        return;
      }

      setIsLoadingEvents(true);
      getReportFilterEventsByYearService({ token, year })
        .then((response) => {
          setListEvents(
            (response.data?.output ?? [])
              .map((event) => ({
                value: event.idNumber,
                label: event.eventEditionName,
              }))
              .sort((a, b) => a.label.localeCompare(b.label))
          );
        })
        .catch(() => {
          setListEvents([]);
          showEventsLoadError();
        })
        .finally(() => {
          setIsLoadingEvents(false);
        });
    },
    [token, showEventsLoadError]
  );

  useEffect(() => {
    fetchYears();
  }, [fetchYears]);

  // A lista completa de eventos só é necessária para resolver o eventId da URL;
  // na seleção manual os eventos vêm do filtro por ano.
  useEffect(() => {
    if (!hasEventIdParam) return;

    const fetchEvents = async () => {
      setIsLoadingEvents(true);
      try {
        const response = await api.get("/admin/events", {
          headers: { Authorization: `Bearer ${token}` },
          params: { active: true },
        });

        const events = response.data.output.map((event) => ({
          value: event.idNumber,
          label: event.eventEditionName,
        }));

        setListEvents(events);
      } catch (error) {
        showEventsLoadError();
      } finally {
        setIsLoadingEvents(false);
      }
    };

    fetchEvents();
  }, [hasEventIdParam, token, showEventsLoadError]);

  // Quando recebemos o eventId pela URL, pré-seleciona o evento automaticamente.
  useEffect(() => {
    if (!hasEventIdParam || selectedEvent || listEvents.length === 0) return;

    const parsedEventId = parseInt(eventIdParam, 10);
    const matched = listEvents.find((event) => event.value === parsedEventId);
    if (matched) {
      setSelectedEvent(matched);
      setEventModalOpen(false);
    }
  }, [hasEventIdParam, eventIdParam, listEvents, selectedEvent]);

  useEffect(() => {
    if (!selectedEvent) return;

    const fetchAreas = async () => {
      setIsLoadingAreas(true);
      try {
        const data = await getEventAreas(token, selectedEvent.value);
        setAllAreas(data.totems);

        if (data.totalPositionsLimit) {
          setMaxPositions(data.totalPositionsLimit);
          setValue("totalPositions", String(data.totalPositionsLimit), {
            shouldValidate: formState.isSubmitted,
          });
        }
      } catch (error) {
        addToast({
          type: "error",
          title: intl.formatMessage({ id: "error" }),
          description:
            error.response?.data?.message ||
            intl.formatMessage({ id: "totem_position.error_loading_areas" }),
        });
      } finally {
        setIsLoadingAreas(false);
      }
    };

    fetchAreas();
  }, [selectedEvent, token, intl, addToast, setValue, formState.isSubmitted]);

  useEffect(() => {
    if (!selectedEvent) return;

    const fetchConfigurations = async () => {
      setIsLoadingConfigurations(true);
      setIsInitializing(true);

      try {
        const configs = await getPositionConfigurations(
          token,
          selectedEvent.value
        );
        setExistingConfigurations(configs.configurations);

        const totalPositions = configs.totalPositions
          ? String(configs.totalPositions)
          : "";

        const ranges =
          configs.configurations.length > 0
            ? configs.configurations.map((config) => ({
                id: config.id,
                areaId: { value: config.areaId, label: config.areaName },
                positionStart: config.positionStart,
                positionEnd: config.positionEnd,
                canEdit: config.canEdit,
                canDelete: config.canDelete,
                hasActiveSessions: config.hasActiveSessions,
                hasPrintedCredentials: config.hasPrintedCredentials,
              }))
            : [
                {
                  areaId: null,
                  positionStart: "",
                  positionEnd: "",
                  canEdit: true,
                  canDelete: true,
                },
              ];

        // Usar reset() ao invés de setValue() para resetar completamente o estado do formulário
        // Isso evita que mensagens de erro apareçam ao reabrir um evento já salvo
        reset(
          {
            totalPositions,
            ranges,
          },
          {
            keepDefaultValues: false,
            keepErrors: false,
            keepDirty: false,
            keepIsSubmitted: false,
            keepTouched: false,
            keepIsValid: false,
            keepSubmitCount: false,
          }
        );

        if (configs.totalPositions) {
          setMaxPositions(configs.totalPositions);
        }
      } catch (error) {
        addToast({
          type: "error",
          title: intl.formatMessage({ id: "error" }),
          description:
            error.response?.data?.message ||
            intl.formatMessage({
              id: "totem_position.error_loading_configurations",
            }),
        });
      } finally {
        setIsLoadingConfigurations(false);
        setIsInitializing(false);
      }
    };

    fetchConfigurations();
  }, [selectedEvent, token, intl, addToast, reset]);

  useEffect(() => {
    if (isInitializing) return;

    const value = parseInt(watchedTotalPositions, 10);
    if (!Number.isNaN(value) && value >= TOTAL_POSITIONS_MIN) {
      setMaxPositions(value);
    }
  }, [watchedTotalPositions, isInitializing]);

  const handleEventSelect = useCallback(
    (event) => {
      setSelectedEvent(event);
      setEventModalOpen(false);

      reset({
        totalPositions: "",
        ranges: [
          {
            areaId: null,
            positionStart: "",
            positionEnd: "",
            canEdit: true,
            canDelete: true,
          },
        ],
      });

      setMaxPositions(null);
      setAllAreas([]);
      setExistingConfigurations([]);
      clearErrors();
    },
    [reset, clearErrors]
  );

  const handleAddRange = useCallback(() => {
    if (!maxPositions) {
      addToast({
        type: "warning",
        title: intl.formatMessage({ id: "warning" }),
        description: intl.formatMessage({
          id: "totem_position.set_total_positions_first",
        }),
      });
      return;
    }

    if (configuredCount >= maxPositions) {
      addToast({
        type: "warning",
        title: intl.formatMessage({ id: "warning" }),
        description: intl.formatMessage({
          id: "totem_position.all_positions_configured",
        }),
      });
      return;
    }

    if (!hasAvailableAreas) {
      addToast({
        type: "warning",
        title: intl.formatMessage({ id: "warning" }),
        description: intl.formatMessage({
          id: "totem_position.no_areas_available",
        }),
      });
      return;
    }

    const lastRange = watchedRanges[watchedRanges.length - 1];
    if (
      lastRange &&
      (!lastRange.areaId || !lastRange.positionStart || !lastRange.positionEnd)
    ) {
      addToast({
        type: "warning",
        title: intl.formatMessage({ id: "warning" }),
        description: intl.formatMessage({
          id: "totem_position.complete_current_range_first",
        }),
      });
      return;
    }

    append({
      areaId: null,
      positionStart: "",
      positionEnd: "",
      canEdit: true,
      canDelete: true,
    });
  }, [
    maxPositions,
    configuredCount,
    hasAvailableAreas,
    watchedRanges,
    append,
    addToast,
    intl,
  ]);

  const handleRemoveRange = useCallback(
    (index) => {
      const range = watchedRanges[index];

      if (range.id && !range.canDelete) {
        addToast({
          type: "error",
          title: intl.formatMessage({ id: "error" }),
          description: intl.formatMessage({
            id: range.hasPrintedCredentials
              ? "totem_position.cannot_delete_has_printed_credentials"
              : "totem_position.cannot_delete_has_active_sessions",
          }),
        });
        return;
      }

      remove(index);
    },
    [watchedRanges, remove, addToast, intl]
  );

  const extractRangeIndex = useCallback((description) => {
    const match = description?.match(/ranges\[(\d+)\]/);
    return match ? parseInt(match[1], 10) : null;
  }, []);

  const handleApiError = useCallback(
    (error) => {
      const errorCode = error.response?.data?.code;
      const errorDescription = error.response?.data?.description;

      // Mapeamento de códigos de erro para chaves i18n
      const errorMap = {
        // Autenticação/Autorização
        '-1001': 'totem_position.error.unauthorized',
        '-1002': 'totem_position.error.forbidden',

        // Validações de Campo
        '-50100': 'totem_position.error.event_id_required',
        '-50101': 'totem_position.error.area_id_required',
        '-50102': 'totem_position.error.start_position_required',
        '-50103': 'totem_position.error.end_position_required',
        '-50104': 'totem_position.error.start_position_min',
        '-50105': 'totem_position.error.end_position_min',
        '-50106': 'totem_position.error.total_positions_min',
        '-50107': 'totem_position.error.start_greater_than_end',

        // Validações de Negócio
        '-50027': 'totem_position.error.limit_requires_ranges',
        '-50021': 'totem_position.error.cannot_delete_active_sessions',
        '-50022': 'totem_position.error.area_duplicated_in_request',
        '-50023': 'totem_position.error.area_not_belongs_to_event',
        '-50024': 'totem_position.error.overlap_in_request',
        '-50025': 'totem_position.error.overlap_with_existing',
        '-50020': 'totem_position.error.cannot_edit_active_sessions',
      };

      if (
        typeof errorCode === "number" &&
        errorCode >= -50107 &&
        errorCode <= -50100
      ) {
        const rangeIndex = extractRangeIndex(errorDescription || "");

        let fieldName = "";
        let fieldNameKey = "";

        switch (errorCode) {
          case -50101:
            fieldName = "areaId";
            fieldNameKey = "totem_position.error.field.area";
            break;
          case -50102:
          case -50104:
            fieldName = "positionStart";
            fieldNameKey = "totem_position.error.field.start_position";
            break;
          case -50103:
          case -50105:
            fieldName = "positionEnd";
            fieldNameKey = "totem_position.error.field.end_position";
            break;
          case -50107:
            if (rangeIndex !== null && rangeIndex < watchedRanges.length) {
              setError(`ranges.${rangeIndex}.positionStart`, {
                type: "manual",
                message: intl.formatMessage({
                  id: errorMap[String(errorCode)],
                }),
              });
              setError(`ranges.${rangeIndex}.positionEnd`, {
                type: "manual",
                message: intl.formatMessage({
                  id: errorMap[String(errorCode)],
                }),
              });
            }
            fieldNameKey = "totem_position.error.field.start_position";
            break;
          default:
            break;
        }

        if (
          rangeIndex !== null &&
          rangeIndex < watchedRanges.length &&
          fieldName
        ) {
          setError(`ranges.${rangeIndex}.${fieldName}`, {
            type: "manual",
            message: intl.formatMessage({ id: errorMap[String(errorCode)] }),
          });
        }

        const rangeNumber = rangeIndex !== null ? rangeIndex + 1 : "?";
        const fieldError = fieldNameKey
          ? `${intl.formatMessage({ id: fieldNameKey })} - ${intl.formatMessage(
              {
                id: errorMap[String(errorCode)],
              }
            )}`
          : intl.formatMessage({ id: errorMap[String(errorCode)] });

        addToast({
          type: "error",
          title: intl.formatMessage({
            id: "totem_position.error.field_validation",
          }),
          description: intl.formatMessage(
            { id: "totem_position.error.range_field_error" },
            { rangeNumber, fieldError }
          ),
        });

        return;
      }

      const i18nKey = errorMap[String(errorCode)];
      if (i18nKey) {
        addToast({
          type: "error",
          title: intl.formatMessage({ id: "error" }),
          description: intl.formatMessage({ id: i18nKey }),
        });
        return;
      }

      addToast({
        type: "error",
        title: intl.formatMessage({ id: "error" }),
        description:
          errorDescription ||
          intl.formatMessage({ id: "totem_position.error.generic" }),
      });
    },
    [addToast, intl, watchedRanges, extractRangeIndex, setError]
  );

  const onSubmit = useCallback(
    async (data) => {
      if (data.ranges && data.ranges.length > 0) {
        const overlap = validateNoOverlap(data.ranges);
        if (overlap) {
          addToast({
            type: "error",
            title: intl.formatMessage({ id: "error" }),
            description: overlap,
          });
          return;
        }
      }

      setIsSaving(true);

      try {
        const rangesToSave = data.ranges.map((range) => ({
          areaId: range.areaId.value,
          positionStart: range.positionStart,
          positionEnd: range.positionEnd,
        }));

        const totalPositionsToSave =
          data.totalPositions && data.totalPositions !== "0"
            ? parseInt(data.totalPositions, 10)
            : null;

        await batchPositionConfiguration(
          token,
          selectedEvent.value,
          totalPositionsToSave,
          rangesToSave
        );

        addToast({
          type: "success",
          title: intl.formatMessage({ id: "success" }),
          description: intl.formatMessage({
            id: "totem_position.save_success",
          }),
        });

        setSelectedEvent(null);
        setAllAreas([]);
        setExistingConfigurations([]);
        setMaxPositions(null);

        reset({
          totalPositions: "",
          ranges: [
            {
              areaId: null,
              positionStart: "",
              positionEnd: "",
              canEdit: true,
              canDelete: true,
            },
          ],
        });

        history.push(LIST_ROUTE);
      } catch (error) {
        handleApiError(error);
      } finally {
        setIsSaving(false);
      }
    },
    [addToast, handleApiError, intl, reset, selectedEvent, token, history]
  );

  const focusFirstError = useCallback(
    (errors) => {
      if (errors?.totalPositions) {
        setFocus("totalPositions");
        return;
      }

      if (Array.isArray(errors?.ranges)) {
        const idx = errors.ranges.findIndex(Boolean);
        if (idx >= 0) {
          const rangeError = errors.ranges[idx];
          if (rangeError?.areaId) return setFocus(`ranges.${idx}.areaId`);
          if (rangeError?.positionStart)
            return setFocus(`ranges.${idx}.positionStart`);
          if (rangeError?.positionEnd)
            return setFocus(`ranges.${idx}.positionEnd`);
        }
      }
    },
    [setFocus]
  );

  const onInvalid = useCallback(
    (errors) => {
      // Sem toast: apenas mostrar mensagens inline (RHF já popula formState.errors).
      focusFirstError(errors);
    },
    [focusFirstError]
  );

  return (
    <AnimatedMainContainer>
      <Spin spinning={isSaving} >
      <EventSelectionModal
        isOpen={eventModalOpen}
        onClose={() => setEventModalOpen(false)}
        onEventSelect={handleEventSelect}
        events={listEvents}
        years={listYears}
        onYearSelect={fetchEventsByYear}
        loading={isLoadingEvents}
      />

      <Container>
        <TitleContainer withSelectEvent>
          <Title>
            {intl.formatMessage({ id: "totem_position.page_title" })} -
          </Title>

          <Title sub>
            {selectedEvent
              ? selectedEvent.label
              : intl.formatMessage({ id: "choose_event" })}
            <HiOutlinePencil
              size={20}
              style={{ cursor: "pointer" }}
              onClick={() => setEventModalOpen(true)}
            />
          </Title>
        </TitleContainer>

        {selectedEvent && (
          <ContentContainer>
            <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
              <FormSection>
                <SectionTitle>
                  {intl.formatMessage({ id: "totem_position.basic_config" })}
                </SectionTitle>

                <InputGroup>
                  <Label>{intl.formatMessage({ id: "event" })}</Label>
                  <div>{selectedEvent.label}</div>
                </InputGroup>

                <InputGroup>
                  <ControlledInput
                    name="totalPositions"
                    control={control}
                    formState={isInitializing ? { errors: {} } : formState}
                    type="number"
                    min={TOTAL_POSITIONS_MIN}
                    maxLength={3}
                    placeholder="Ex: 500"
                    label="totem_position.total_positions"
                    required={true}
                    onKeyDown={(e) => {
                      if (
                        e.key === "-" ||
                        e.key === "+" ||
                        e.key === "e" ||
                        e.key === "E"
                      ) {
                        e.preventDefault();
                      }
                    }}
                  />
                  <FieldHint>
                    {intl.formatMessage({
                      id: "totem_position.total_positions_hint",
                    })}
                  </FieldHint>
                </InputGroup>

                {hasRangesWithData && !hasTotalPositionsValid && (
                  <ValidationMessage type="warning">
                    <MdWarning size={20} />
                    <span>
                      {intl.formatMessage({
                        id: "totem_position.total_positions_required_for_ranges",
                      })}
                    </span>
                  </ValidationMessage>
                )}

                {hasTotalPositionsValid && (
                  <>
                    <PositionCounter>
                      <CounterLabel>
                        {intl.formatMessage({
                          id: "totem_position.configured_positions",
                        })}
                        :
                      </CounterLabel>
                      <CounterValue
                        isComplete={configuredCount === maxPositions}
                        isOverLimit={configuredCount > maxPositions}
                      >
                        {configuredCount} / {maxPositions || 0}
                      </CounterValue>
                    </PositionCounter>

                    {hasOverlap && (
                      <ValidationMessage type="error">
                        <MdError size={20} />
                        <span>{overlapError}</span>
                      </ValidationMessage>
                    )}

                    {exceedsLimit && (
                      <ValidationMessage type="error">
                        <MdError size={20} />
                        <span>
                          {intl.formatMessage(
                            { id: "totem_position.total_exceeds_limit" },
                            { total: configuredCount, limit: maxPositions }
                          )}
                        </span>
                      </ValidationMessage>
                    )}

                    {hasInvalidExpansion && (
                      <ValidationMessage type="error">
                        <MdError size={20} />
                        <span>
                          {watchedRanges
                            .map((range) => validateExpansion(range))
                            .find((error) => error !== null)}
                        </span>
                      </ValidationMessage>
                    )}
                  </>
                )}
              </FormSection>

              {hasTotalPositionsValid && (
                <FormSection>
                  <SectionTitle>
                    {intl.formatMessage({
                      id: "totem_position.position_ranges",
                    })}
                  </SectionTitle>

                  <RangesList>
                    {fields.map((field, index) => (
                      <PositionRangeItem
                        key={field.id}
                        index={index}
                        control={control}
                        formState={formState}
                        availableAreas={availableAreasForIndex(index)}
                        maxPositions={maxPositions}
                        range={watchedRanges[index]}
                        validationError={validateRangeComplete(
                          watchedRanges[index]
                        )}
                        onRemove={() => handleRemoveRange(index)}
                        isLoading={isLoadingAreas}
                        isInitializing={isInitializing}
                      />
                    ))}
                  </RangesList>

                  {canAddNewRange && (
                    <AddRangeButton type="button" onClick={handleAddRange}>
                      + {intl.formatMessage({ id: "totem_position.add_range" })}
                    </AddRangeButton>
                  )}
                </FormSection>
              )}

              <ContainerButtons>
                <ActionButton
                  type="button"
                  cancel
                  onClick={() => {
                    setSelectedEvent(null);
                    setAllAreas([]);
                    setExistingConfigurations([]);
                    setMaxPositions(null);

                    reset({
                      totalPositions: "",
                      ranges: [
                        {
                          areaId: null,
                          positionStart: "",
                          positionEnd: "",
                          canEdit: true,
                          canDelete: true,
                        },
                      ],
                    });

                    clearErrors();
                    history.push(LIST_ROUTE);
                  }}
                >
                  <MdOutlineCancel size={20} />
                  {intl.formatMessage({ id: "cancel" })}
                </ActionButton>

                <ActionButton
                  save
                  type="submit"
                  disabled={(() => {
                    const conditions = {
                      isSaving,
                      hasOverlap,
                      exceedsLimit,
                      hasInvalidExpansion,
                      hasFormErrors: !formState.isValid,
                    };

                    return Object.values(conditions).some(Boolean);
                  })()}
                >
                  <FaRegSave />
                  {intl.formatMessage({ id: "save" })}
                </ActionButton>
              </ContainerButtons>
            </form>
          </ContentContainer>
        )}
      </Container>
      </Spin>
    </AnimatedMainContainer>
  );
};

export default TotemPositionConfiguration;
